<template>
    <div class="vd-page relative min-h-screen w-full">
        <div class="vd-page__glow" aria-hidden="true"></div>
        <div class="container mx-auto px-4 sm:px-8 py-8 relative">

            <nav class="vd-breadcrumb mb-6">
                <router-link to="/dashboard" class="vd-breadcrumb__link">Dashboard</router-link>
                <span class="vd-breadcrumb__sep">/</span>
                <router-link to="/volunteering-and-rewards-hub" class="vd-breadcrumb__link">Volunteering &amp; Rewards Hub</router-link>
                <span class="vd-breadcrumb__sep">/</span>
                <span class="vd-breadcrumb__current">{{ opportunity && opportunity.title }}</span>
            </nav>

            <div v-if="loading" class="flex justify-center py-20">
                <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
            </div>

            <template v-else-if="opportunity">
                <header class="vd-hero mb-8">
                    <div class="vd-hero__accent" aria-hidden="true"></div>
                    <div class="vd-hero__inner">
                        <div class="vd-hero__copy">
                            <div class="vd-hero__topline">
                                <span class="vd-hero__kicker">
                                    <font-awesome-icon :icon="['fas', 'hand-holding-heart']" />
                                    Volunteering Opportunity
                                </span>
                                <div class="vd-hero__topline-actions">
                                    <router-link
                                        v-if="isAdmin && opportunity && opportunity.token && hasPermission('volunteering-application-index')"
                                        :to="'/volunteering-and-rewards-hub/' + opportunity.token + '/applications'"
                                        class="vd-back-link"
                                    >
                                        <font-awesome-icon :icon="['fas', 'clipboard-list']" />
                                        <span>View Applications</span>
                                    </router-link>
                                    <router-link to="/volunteering-and-rewards-hub" class="vd-back-link">
                                        <font-awesome-icon :icon="['fas', 'arrow-left']" />
                                        <span>Back to hub</span>
                                    </router-link>
                                </div>
                            </div>
                            <h1 class="vd-hero__title">{{ opportunity.title }}</h1>
                            <div class="vd-hero__meta">
                                <span class="vd-badge" :class="'vd-badge--' + opportunity.status">{{ formatStatus(opportunity.status) }}</span>
                                <span v-if="opportunity.is_accepting_applications" class="vd-badge vd-badge--open">
                                    <span class="vd-badge__dot"></span>
                                    Accepting Applications
                                </span>
                                <span v-else-if="opportunity.has_ended" class="vd-badge vd-badge--ended">Event Ended</span>
                            </div>
                            <div class="vd-hero__stats">
                                <div class="vd-hero-stat">
                                    <font-awesome-icon :icon="['fas', 'clock']" class="vd-hero-stat__icon" />
                                    <div>
                                        <p class="vd-hero-stat__label">Expected</p>
                                        <p class="vd-hero-stat__value">{{ formatHours(opportunity.expected_hours) }}</p>
                                    </div>
                                </div>
                                <div class="vd-hero-stat">
                                    <font-awesome-icon :icon="['fas', 'calendar']" class="vd-hero-stat__icon" />
                                    <div>
                                        <p class="vd-hero-stat__label">Deadline</p>
                                        <p class="vd-hero-stat__value">{{ formatDateTime(opportunity.application_deadline) }}</p>
                                    </div>
                                </div>
                                <div class="vd-hero-stat">
                                    <font-awesome-icon :icon="['fas', 'users']" class="vd-hero-stat__icon" />
                                    <div>
                                        <p class="vd-hero-stat__label">Capacity</p>
                                        <p class="vd-hero-stat__value">{{ opportunity.max_volunteers || 'Unlimited' }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
                    <div class="lg:col-span-2 space-y-6">

                        <section class="vd-panel">
                            <div class="vd-panel__header">
                                <div class="vd-panel__title-wrap">
                                    <span class="vd-panel__icon"><font-awesome-icon :icon="['fas', 'info-circle']" /></span>
                                    <div>
                                        <h2 class="vd-panel__title">About this opportunity</h2>
                                        <p class="vd-panel__subtitle">Overview and key details for volunteers</p>
                                    </div>
                                </div>
                            </div>
                            <div class="vd-panel__body">
                                <p class="vd-description">{{ opportunity.description }}</p>
                                <div class="vd-detail-grid">
                                    <div class="vd-detail-item">
                                        <span class="vd-detail-item__icon vd-detail-item__icon--location">
                                            <font-awesome-icon :icon="['fas', 'location-dot']" />
                                        </span>
                                        <div class="min-w-0">
                                            <p class="vd-detail-item__label">Location</p>
                                            <a
                                                v-if="locationMapsUrl"
                                                :href="locationMapsUrl"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                class="vd-detail-item__value vd-location-link"
                                            >
                                                <span>{{ opportunity.location_name }}</span>
                                                <font-awesome-icon :icon="['fas', 'external-link-alt']" class="vd-location-link__icon" />
                                            </a>
                                            <p v-else class="vd-detail-item__value">{{ opportunity.location_name }}</p>
                                            <p v-if="opportunity.address" class="vd-detail-item__sub">{{ opportunity.address }}</p>
                                        </div>
                                    </div>
                                    <div class="vd-detail-item">
                                        <span class="vd-detail-item__icon vd-detail-item__icon--hours">
                                            <font-awesome-icon :icon="['fas', 'clock']" />
                                        </span>
                                        <div>
                                            <p class="vd-detail-item__label">Expected Hours</p>
                                            <p class="vd-detail-item__value">{{ formatHours(opportunity.expected_hours) }}</p>
                                        </div>
                                    </div>
                                    <div class="vd-detail-item">
                                        <span class="vd-detail-item__icon vd-detail-item__icon--deadline">
                                            <font-awesome-icon :icon="['fas', 'calendar']" />
                                        </span>
                                        <div>
                                            <p class="vd-detail-item__label">Application Deadline</p>
                                            <p class="vd-detail-item__value">{{ formatDateTime(opportunity.application_deadline) }}</p>
                                        </div>
                                    </div>
                                    <div class="vd-detail-item">
                                        <span class="vd-detail-item__icon vd-detail-item__icon--capacity">
                                            <font-awesome-icon :icon="['fas', 'users']" />
                                        </span>
                                        <div>
                                            <p class="vd-detail-item__label">Max Volunteers</p>
                                            <p class="vd-detail-item__value">{{ opportunity.max_volunteers || 'Unlimited' }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section class="vd-panel">
                            <div class="vd-panel__header">
                                <div class="vd-panel__title-wrap">
                                    <span class="vd-panel__icon"><font-awesome-icon :icon="['fas', 'calendar-days']" /></span>
                                    <div>
                                        <h2 class="vd-panel__title">Schedule</h2>
                                        <p class="vd-panel__subtitle">Session dates and times</p>
                                    </div>
                                </div>
                            </div>
                            <div class="vd-panel__body">
                                <ul v-if="opportunity.schedules && opportunity.schedules.length" class="vd-schedule-list">
                                    <li v-for="(slot, idx) in opportunity.schedules" :key="idx" class="vd-schedule-item">
                                        <span class="vd-schedule-item__num">{{ idx + 1 }}</span>
                                        <div class="vd-schedule-item__body">
                                            <p class="vd-schedule-item__date">{{ formatScheduleSlot(slot) }}</p>
                                        </div>
                                    </li>
                                </ul>
                                <div v-else class="vd-empty-inline">
                                    <font-awesome-icon :icon="['fas', 'calendar-xmark']" />
                                    <p>No schedule has been published yet.</p>
                                </div>
                            </div>
                        </section>

                        <section v-if="opportunity.attachments && opportunity.attachments.length" class="vd-panel">
                            <div class="vd-panel__header">
                                <div class="vd-panel__title-wrap">
                                    <span class="vd-panel__icon"><font-awesome-icon :icon="['fas', 'paperclip']" /></span>
                                    <div>
                                        <h2 class="vd-panel__title">Attachments</h2>
                                        <p class="vd-panel__subtitle">Supporting documents for this opportunity</p>
                                    </div>
                                </div>
                            </div>
                            <div class="vd-panel__body">
                                <ul class="vd-attachment-list">
                                    <li v-for="(att, idx) in opportunity.attachments" :key="idx">
                                        <button
                                            v-if="att.file_path"
                                            type="button"
                                            class="vd-attachment-link"
                                            :disabled="previewLoading && previewAttachment && previewAttachment.file_path === att.file_path"
                                            @click="openAttachmentPreview(att)"
                                        >
                                            <span class="vd-attachment-link__icon">
                                                <font-awesome-icon :icon="['fas', 'file-alt']" />
                                            </span>
                                            <span class="vd-attachment-link__text">
                                                {{ previewLoading && previewAttachment && previewAttachment.file_path === att.file_path ? 'Loading...' : att.name }}
                                            </span>
                                            <font-awesome-icon :icon="['fas', 'eye']" class="vd-attachment-link__action" />
                                        </button>
                                        <span v-else class="vd-attachment-fallback">{{ att.name }}</span>
                                    </li>
                                </ul>
                            </div>
                        </section>

                        <section v-if="application && showConversation" class="vd-panel">
                            <div class="vd-panel__header">
                                <div class="vd-panel__title-wrap">
                                    <span class="vd-panel__icon"><font-awesome-icon :icon="['fas', 'comments']" /></span>
                                    <div>
                                        <h2 class="vd-panel__title">Application Conversation</h2>
                                        <p class="vd-panel__subtitle">Messages between you and the reviewing team</p>
                                    </div>
                                </div>
                            </div>
                            <div class="vd-panel__body">
                                <volunteering-conversation type="application" :record-id="application.id" :can-reply="canReplyApplication" />
                                <div v-if="application.status === 'returned' && isStudent" class="vd-inline-action">
                                    <button type="button" class="vd-btn-primary" @click="openResubmitModal">Resubmit Application</button>
                                </div>
                            </div>
                        </section>

                        <section v-if="showHoursForm" class="vd-panel">
                            <div class="vd-panel__header">
                                <div class="vd-panel__title-wrap">
                                    <span class="vd-panel__icon"><font-awesome-icon :icon="['fas', 'hourglass-half']" /></span>
                                    <div>
                                        <h2 class="vd-panel__title">Submit Volunteering Hours</h2>
                                        <p class="vd-panel__subtitle">Select the dates you attended and upload proof</p>
                                    </div>
                                </div>
                            </div>
                            <div class="vd-panel__body">
                                <form class="vd-form" @submit.prevent="submitHours">
                                    <div class="vd-form-card">
                                        <h3 class="vd-form-card__title">Dates attended</h3>
                                        <div v-for="(slot, idx) in opportunity.schedules" :key="idx" class="vd-checkbox-row">
                                            <input :id="'slot-' + idx" v-model="hoursForm.selected" type="checkbox" :value="idx" />
                                            <label :for="'slot-' + idx">{{ formatScheduleSlot(slot) }}</label>
                                        </div>
                                    </div>
                                    <div class="vd-form-row">
                                        <label class="vd-label">Actual Hours *</label>
                                        <input v-model="hoursForm.actual_hours" type="number" step="0.5" min="0.5" class="vd-input w-full" required />
                                        <p class="vd-hint">Calculated from selection: {{ calculatedSelectedHours }} hrs</p>
                                    </div>
                                    <div class="vd-form-row">
                                        <label class="vd-label">Notes</label>
                                        <textarea v-model="hoursForm.notes" class="vd-input w-full" rows="2"></textarea>
                                    </div>
                                    <div class="vd-form-card">
                                        <h3 class="vd-form-card__title">Proof files *</h3>
                                        <p class="vd-hint mb-3">PDF, JPG, or PNG — max 5MB each</p>
                                        <div v-for="(proof, idx) in hoursForm.proof_files" :key="idx" class="vd-proof-row">
                                            <input v-model="proof.name" placeholder="Display name" class="vd-input flex-1" />
                                            <input type="file" accept=".pdf,.jpg,.jpeg,.png" class="vd-file-input" @change="onProofFile($event, idx)" />
                                            <button type="button" class="vd-icon-btn vd-icon-btn--danger" @click="hoursForm.proof_files.splice(idx, 1)">
                                                <font-awesome-icon :icon="['fas', 'times']" />
                                            </button>
                                        </div>
                                        <button type="button" class="vd-btn-ghost" @click="addProofFile">
                                            <font-awesome-icon :icon="['fas', 'plus']" />
                                            <span>Add proof file</span>
                                        </button>
                                    </div>
                                    <div class="vd-form-actions">
                                        <button type="submit" class="vd-btn-primary" :disabled="submittingHours">
                                            {{ submittingHours ? 'Submitting...' : 'Submit Hours' }}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </section>

                        <section v-if="hourSubmission && showHoursConversation" class="vd-panel">
                            <div class="vd-panel__header">
                                <div class="vd-panel__title-wrap">
                                    <span class="vd-panel__icon"><font-awesome-icon :icon="['fas', 'comment']" /></span>
                                    <div>
                                        <h2 class="vd-panel__title">Hours Verification Conversation</h2>
                                        <p class="vd-panel__subtitle">Discussion about your submitted hours</p>
                                    </div>
                                </div>
                            </div>
                            <div class="vd-panel__body">
                                <volunteering-conversation type="hours" :record-id="hourSubmission.id" :can-reply="canReplyHours" />
                            </div>
                        </section>

                        <section v-if="isAdmin && hourSubmission && hourSubmission.status === 'pending'" class="vd-panel">
                            <div class="vd-panel__header">
                                <div class="vd-panel__title-wrap">
                                    <span class="vd-panel__icon"><font-awesome-icon :icon="['fas', 'clipboard-check']" /></span>
                                    <div>
                                        <h2 class="vd-panel__title">Submitted Hours Details</h2>
                                        <p class="vd-panel__subtitle">Review student hours before approving</p>
                                    </div>
                                </div>
                            </div>
                            <div class="vd-panel__body">
                                <div class="vd-detail-grid">
                                    <div v-if="hourSubmission.user" class="vd-detail-item">
                                        <span class="vd-detail-item__icon"><font-awesome-icon :icon="['fas', 'user']" /></span>
                                        <div>
                                            <p class="vd-detail-item__label">Student</p>
                                            <p class="vd-detail-item__value">{{ hourSubmission.user.name }} ({{ hourSubmission.student_id }})</p>
                                        </div>
                                    </div>
                                    <div class="vd-detail-item">
                                        <span class="vd-detail-item__icon"><font-awesome-icon :icon="['fas', 'clock']" /></span>
                                        <div>
                                            <p class="vd-detail-item__label">Submitted Hours</p>
                                            <p class="vd-detail-item__value">{{ formatHours(hourSubmission.actual_hours) }}</p>
                                        </div>
                                    </div>
                                    <div v-if="hourSubmission.submitted_at" class="vd-detail-item">
                                        <span class="vd-detail-item__icon"><font-awesome-icon :icon="['fas', 'calendar']" /></span>
                                        <div>
                                            <p class="vd-detail-item__label">Submitted</p>
                                            <p class="vd-detail-item__value">{{ formatDateTime(hourSubmission.submitted_at) }}</p>
                                        </div>
                                    </div>
                                </div>
                                <p v-if="hourSubmission.notes" class="vd-notes-block"><strong>Notes:</strong> {{ hourSubmission.notes }}</p>
                                <div v-if="hourSubmission.proof_files && hourSubmission.proof_files.length" class="mt-4">
                                    <p class="vd-label">Proof files</p>
                                    <ul class="vd-attachment-list">
                                        <li v-for="(proof, idx) in hourSubmission.proof_files" :key="idx">
                                            <button
                                                v-if="proof.file_path"
                                                type="button"
                                                class="vd-attachment-link"
                                                :disabled="previewLoading && previewAttachment && previewAttachment.file_path === proof.file_path"
                                                @click="openProofPreview(proof)"
                                            >
                                                <span class="vd-attachment-link__icon">
                                                    <font-awesome-icon :icon="['fas', 'file-alt']" />
                                                </span>
                                                <span class="vd-attachment-link__text">
                                                    {{ previewLoading && previewAttachment && previewAttachment.file_path === proof.file_path ? 'Loading...' : proof.name }}
                                                </span>
                                                <font-awesome-icon :icon="['fas', 'eye']" class="vd-attachment-link__action" />
                                            </button>
                                            <span v-else class="vd-attachment-fallback">{{ proof.name }}</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </section>
                    </div>

                    <div class="space-y-5 vd-sidebar">
                        <aside v-if="isStudent" class="vd-sidebar-card vd-sidebar-card--action">
                            <div class="vd-sidebar-card__head">
                                <h3 class="vd-sidebar-card__title">Your Application</h3>
                                <p class="vd-sidebar-card__sub">Track your status and next steps</p>
                            </div>
                            <div v-if="!application">
                                <button v-if="canApply" type="button" class="vd-btn-primary w-full" :disabled="applying" @click="apply">
                                    {{ applying ? 'Applying...' : 'Apply Now' }}
                                </button>
                                <p v-else class="vd-muted">Applications are not currently open for this opportunity.</p>
                            </div>
                            <div v-else class="vd-sidebar-status">
                                <span class="vd-badge vd-badge--lg" :class="'vd-badge--' + application.status">{{ formatStatus(application.status) }}</span>
                                <button v-if="application.status === 'pending'" type="button" class="vd-btn-outline w-full mt-4" @click="withdraw">
                                    Withdraw Application
                                </button>
                            </div>
                        </aside>

                        <aside v-if="isAdmin && application" class="vd-sidebar-card">
                            <div class="vd-sidebar-card__head">
                                <h3 class="vd-sidebar-card__title">Review Application</h3>
                                <p
                                    v-if="application.student_id"
                                    class="vd-sidebar-card__sub vd-student-link"
                                    @click.stop="viewStudentProfile(application.student_id)"
                                >
                                    {{ application.user && application.user.name }} ({{ application.student_id }})
                                </p>
                                <p v-else class="vd-sidebar-card__sub">
                                    {{ application.user && application.user.name }} ({{ application.student_id }})
                                </p>
                            </div>
                            <div v-if="application.status === 'pending' && canApproveApplications" class="vd-sidebar-actions">
                                <button type="button" class="vd-btn-primary w-full" @click="approveApplication">Approve</button>
                                <button type="button" class="vd-btn-outline w-full" @click="openReasonModal('return-app')">Return</button>
                                <button type="button" class="vd-btn-danger w-full" @click="openReasonModal('reject-app')">Reject</button>
                            </div>
                            <div v-else>
                                <span class="vd-badge vd-badge--lg" :class="'vd-badge--' + application.status">{{ formatStatus(application.status) }}</span>
                            </div>
                        </aside>

                        <aside v-if="isAdmin && hourSubmission && hourSubmission.status === 'pending'" class="vd-sidebar-card">
                            <div class="vd-sidebar-card__head">
                                <h3 class="vd-sidebar-card__title">Review Hours</h3>
                                <p class="vd-sidebar-card__sub">{{ formatHours(hourSubmission.actual_hours) }} submitted for verification</p>
                            </div>
                            <div class="vd-sidebar-actions">
                                <button type="button" class="vd-btn-primary w-full" @click="approveHours">Approve Hours</button>
                                <button type="button" class="vd-btn-outline w-full" @click="openReasonModal('return-hours')">Return</button>
                                <button type="button" class="vd-btn-danger w-full" @click="openReasonModal('reject-hours')">Reject</button>
                            </div>
                        </aside>

                        <aside v-if="application && application.events && application.events.length" class="vd-sidebar-card">
                            <div class="vd-sidebar-card__head">
                                <h3 class="vd-sidebar-card__title">Activity History</h3>
                                <p class="vd-sidebar-card__sub">Recent application events</p>
                            </div>
                            <div class="vd-timeline">
                                <div v-for="evt in application.events" :key="evt.id" class="vd-timeline-item">
                                    <div class="vd-timeline-dot"></div>
                                    <div>
                                        <p class="vd-timeline-title">{{ formatStatus(evt.event_type) }}</p>
                                        <p v-if="evt.note" class="vd-timeline-note">{{ evt.note }}</p>
                                        <p class="vd-timeline-date">{{ formatDateTime(evt.created_at) }}</p>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </template>
        </div>

        <volunteering-styled-modal
            :visible="showReasonModal"
            :title="reasonModalTitle"
            :subtitle="reasonModalSubtitle"
            :icon="['fas', 'comment-dots']"
            width="520px"
            @close="closeReasonModal"
        >
            <div class="vd-modal-card">
                <label class="vd-label">Reason *</label>
                <textarea v-model="reasonText" class="vd-input w-full" rows="4" placeholder="Enter your reason..."></textarea>
            </div>
            <template #footer>
                <div class="vd-modal-footer-actions">
                    <button type="button" class="vd-btn-ghost" @click="closeReasonModal">Cancel</button>
                    <button type="button" class="vd-btn-primary" :disabled="!reasonText.trim() || reasonSubmitting" @click="submitReasonModal">
                        {{ reasonSubmitting ? 'Submitting...' : 'Confirm' }}
                    </button>
                </div>
            </template>
        </volunteering-styled-modal>

        <volunteering-styled-modal
            :visible="showResubmitModal"
            title="Resubmit Application"
            subtitle="Add an optional note for the reviewing team."
            :icon="['fas', 'rotate-left']"
            width="520px"
            @close="closeResubmitModal"
        >
            <div class="vd-modal-card">
                <label class="vd-label">Message (optional)</label>
                <textarea v-model="resubmitMessage" class="vd-input w-full" rows="4" placeholder="Explain any updates or clarifications..."></textarea>
            </div>
            <template #footer>
                <div class="vd-modal-footer-actions">
                    <button type="button" class="vd-btn-ghost" @click="closeResubmitModal">Cancel</button>
                    <button type="button" class="vd-btn-primary" :disabled="resubmitting" @click="submitResubmit">
                        {{ resubmitting ? 'Submitting...' : 'Resubmit' }}
                    </button>
                </div>
            </template>
        </volunteering-styled-modal>

        <volunteering-styled-modal
            :visible="!!previewAttachment"
            :title="previewAttachment ? (previewAttachment.name || previewAttachment.original_filename || 'Attachment') : 'Attachment'"
            :subtitle="previewAttachment ? (previewAttachment.original_filename || '') : ''"
            :icon="previewAttachmentIsPdf ? ['fas', 'file-pdf'] : ['fas', 'search-plus']"
            width="960px"
            height="85vh"
            flush-body
            @close="closeAttachmentPreview"
        >
            <div class="vd-preview-scroll">
                <div class="vd-preview-meta">
                    <span class="vd-preview-chip">
                        <font-awesome-icon :icon="previewAttachmentIsPdf ? ['fas', 'file-pdf'] : ['fas', 'image']" />
                        {{ previewAttachmentIsPdf ? 'PDF document' : 'Image' }}
                    </span>
                </div>
                <div
                    class="vd-preview-frame"
                    :class="{
                        'vd-preview-frame--image': previewAttachmentIsImage,
                        'vd-preview-frame--pdf': previewAttachmentIsPdf,
                    }"
                >
                    <div v-if="previewLoading" class="vd-preview-state">
                        <font-awesome-icon :icon="['fas', 'spinner']" spin class="vd-preview-state__icon" />
                        <span>Loading preview…</span>
                    </div>
                    <img
                        v-else-if="previewAttachmentIsImage && previewDisplayUrl"
                        :src="previewDisplayUrl"
                        :alt="previewAttachment ? previewAttachment.name : 'Attachment'"
                        class="vd-preview-img"
                    />
                    <iframe
                        v-else-if="previewAttachmentIsPdf && previewDisplayUrl"
                        :src="previewDisplayUrl"
                        class="vd-preview-iframe"
                        title="PDF preview"
                    />
                    <div v-else class="vd-preview-state">
                        <font-awesome-icon :icon="['fas', 'exclamation-circle']" class="vd-preview-state__icon vd-preview-state__icon--warn" />
                        <span>Preview unavailable.</span>
                    </div>
                </div>
            </div>
            <template #footer>
                <div class="vd-modal-footer-actions">
                    <button type="button" class="vd-btn-ghost" @click="closeAttachmentPreview">Close</button>
                    <button
                        type="button"
                        class="vd-btn-primary"
                        :disabled="!previewDisplayUrl || previewDownloading"
                        @click="downloadPreviewAttachment"
                    >
                        <font-awesome-icon :icon="['fas', previewDownloading ? 'spinner' : 'download']" :spin="previewDownloading" class="mr-2" />
                        {{ previewDownloading ? 'Downloading…' : 'Download' }}
                    </button>
                </div>
            </template>
        </volunteering-styled-modal>

        <transition name="fade" mode="out-in">
            <base-full-modal
                v-if="showStudentProfileFullModal"
                title="Student Profile"
                @close="closeStudentProfileModal"
            >
                <student-profile :student-id="selectedStudentId"></student-profile>
            </base-full-modal>
        </transition>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import VolunteeringConversation from '@/components/volunteering/VolunteeringConversation.vue';
import VolunteeringStyledModal from '@/components/volunteering/VolunteeringStyledModal.vue';
import StudentProfile from '@/pages/student-profile/index.vue';

const DETAIL_QUERY = gql`
  query VolunteeringDetail($token: String!) {
    volunteeringOpportunityByToken(token: $token) {
      error error_code message
      data {
        id token title description status expected_hours location_name address google_maps_location
        application_deadline max_volunteers
        schedules { start_at end_at }
        allow_hours_exceed_expected
        attachments { name file_path original_filename }
        is_accepting_applications has_ended has_capacity
        viewer_has_applied viewer_application_id viewer_application_status
      }
    }
  }
`;

const APPLICATION_QUERY = gql`
  query VolunteeringApplicationDetail($id: ID!) {
    volunteeringApplication(id: $id) {
      error data {
        id status student_id submitted_at
        user { id name email }
        events { id event_type note created_at }
        hour_submission { id status actual_hours }
      }
    }
  }
`;

const APPLY_MUTATION = gql`
  mutation ApplyVolunteering($token: String!) {
    applyVolunteeringOpportunity(token: $token) { error message already_applied }
  }
`;

const WITHDRAW_MUTATION = gql`
  mutation WithdrawVolunteering($id: ID!) {
    withdrawVolunteeringApplication(application_id: $id) { error message }
  }
`;

const APPROVE_APP = gql`mutation ApproveApp($id: ID!) { approveVolunteeringApplication(application_id: $id) { error message } }`;
const REJECT_APP = gql`mutation RejectApp($id: ID!, $reason: String!) { rejectVolunteeringApplication(application_id: $id, reason: $reason) { error message } }`;
const RETURN_APP = gql`mutation ReturnApp($id: ID!, $reason: String!) { returnVolunteeringApplication(application_id: $id, reason: $reason) { error message } }`;
const RESUBMIT_APP = gql`mutation ResubmitApp($id: ID!, $message: String) { resubmitVolunteeringApplication(application_id: $id, message: $message) { error message } }`;

const SUBMIT_HOURS = gql`
  mutation SubmitHours($input: SubmitVolunteeringHoursInput!) {
    submitVolunteeringHours(input: $input) { error message data { id status } }
  }
`;

const APPROVE_HOURS = gql`mutation ApproveHours($id: ID!) { approveVolunteeringHours(hour_submission_id: $id) { error message } }`;
const REJECT_HOURS = gql`mutation RejectHours($id: ID!, $reason: String!) { rejectVolunteeringHours(hour_submission_id: $id, reason: $reason) { error message } }`;
const RETURN_HOURS = gql`mutation ReturnHours($id: ID!, $reason: String!) { returnVolunteeringHours(hour_submission_id: $id, reason: $reason) { error message } }`;

const HOUR_SUBMISSION_QUERY = gql`
  query VolunteeringHourSubmissionDetail($id: ID!) {
    volunteeringHourSubmission(id: $id) {
      error error_code message
      data {
        id status actual_hours notes submitted_at student_id application_id
        selected_schedule_indices
        user { id name email }
        proof_files { name file_path original_filename }
        application { id status student_id }
      }
    }
  }
`;

const ATTACHMENT_CONTENT_MUTATION = gql`
  mutation GetVolunteeringOpportunityAttachmentContent($opportunity_id: ID!, $file_path: String!) {
    getVolunteeringOpportunityAttachmentContent(opportunity_id: $opportunity_id, file_path: $file_path) {
      error message
      data { content file_type filename }
    }
  }
`;

const PROOF_CONTENT_MUTATION = gql`
  mutation GetVolunteeringHourProofContent($hour_submission_id: ID!, $file_path: String!) {
    getVolunteeringHourProofContent(hour_submission_id: $hour_submission_id, file_path: $file_path) {
      error message
      data { content file_type filename }
    }
  }
`;

export default {
    name: 'VolunteeringOpportunityDetail',
    components: { VolunteeringConversation, VolunteeringStyledModal, StudentProfile },
    props: { token: { type: String, required: true } },
    data() {
        return {
            loading: true,
            opportunity: null,
            application: null,
            hourSubmission: null,
            applying: false,
            submittingHours: false,
            hoursForm: { selected: [], actual_hours: 0, notes: '', proof_files: [{ name: '', file: null }] },
            showReasonModal: false,
            reasonModalAction: '',
            reasonText: '',
            reasonSubmitting: false,
            showResubmitModal: false,
            resubmitMessage: '',
            resubmitting: false,
            previewAttachment: null,
            previewKind: 'attachment',
            previewDisplayUrl: null,
            previewFileType: '',
            previewLoading: false,
            previewDownloading: false,
            showStudentProfileFullModal: false,
            selectedStudentId: null,
        };
    },
    computed: {
        role() { return this.$store.getters.role; },
        isStudent() { return this.role === 'user'; },
        isAdmin() {
            return this.hasPermission('volunteering-application-approve')
                || this.hasPermission('volunteering-hours-approve')
                || this.hasPermission('volunteering-application-index');
        },
        canApproveApplications() {
            return this.hasPermission('volunteering-application-approve');
        },
        previewAttachmentIsImage() {
            return this.isImageAttachment(this.previewAttachment, this.previewFileType);
        },
        previewAttachmentIsPdf() {
            return this.isPdfAttachment(this.previewAttachment, this.previewFileType);
        },
        locationMapsUrl() {
            if (!this.opportunity) return '';
            var maps = this.opportunity.google_maps_location
                ? String(this.opportunity.google_maps_location).trim()
                : '';
            if (maps) {
                return maps;
            }
            var queryParts = [];
            if (this.opportunity.location_name) {
                queryParts.push(String(this.opportunity.location_name).trim());
            }
            if (this.opportunity.address) {
                queryParts.push(String(this.opportunity.address).trim());
            }
            if (!queryParts.length) {
                return '';
            }
            return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(queryParts.join(', '));
        },
        canApply() {
            return this.opportunity && this.opportunity.is_accepting_applications && !this.opportunity.viewer_has_applied;
        },
        showConversation() {
            return this.application && this.application.status !== 'pending';
        },
        canReplyApplication() {
            if (!this.application) return false;
            return this.isStudent || this.hasPermission('reply-volunteering-application-conversation');
        },
        showHoursForm() {
            if (!this.isStudent || !this.application || this.application.status !== 'approved') return false;
            if (!this.opportunity || !this.opportunity.has_ended) return false;
            if (!this.application.hour_submission) return true;
            return this.application.hour_submission.status === 'returned';
        },
        showHoursConversation() {
            return this.hourSubmission && this.hourSubmission.status !== 'pending';
        },
        canReplyHours() {
            return this.isStudent || this.hasPermission('reply-volunteering-hours-conversation');
        },
        calculatedSelectedHours() {
            if (!this.opportunity || !this.opportunity.schedules) return '0.0';
            let total = 0;
            const self = this;
            this.hoursForm.selected.forEach(function (idx) {
                const slot = self.opportunity.schedules[idx];
                if (slot && slot.start_at && slot.end_at) {
                    total += (new Date(slot.end_at) - new Date(slot.start_at)) / 3600000;
                }
            });
            return total.toFixed(1);
        },
        reasonModalTitle() {
            if (this.reasonModalAction === 'reject-app') return 'Reject Application';
            if (this.reasonModalAction === 'return-app') return 'Return Application';
            if (this.reasonModalAction === 'reject-hours') return 'Reject Hours';
            if (this.reasonModalAction === 'return-hours') return 'Return Hours';
            return 'Provide Reason';
        },
        reasonModalSubtitle() {
            return 'A reason is required so the student understands what action was taken.';
        },
    },
    watch: {
        'hoursForm.selected': {
            handler() {
                this.hoursForm.actual_hours = parseFloat(this.calculatedSelectedHours);
            },
            deep: true,
        },
    },
    created() {
        if (!this.hasPermission('volunteering-and-rewards-hub-module')) {
            this.$router.replace('/no-permission');
            return;
        }
        this.fetchDetail();
    },
    beforeUnmount() {
        this.revokePreviewDisplayUrl();
    },
    methods: {
        hasPermission(p) {
            return (this.$store.getters.permissions || '').split(',').includes(p);
        },
        viewStudentProfile(studentId) {
            if (!studentId) return;
            this.selectedStudentId = studentId;
            this.showStudentProfileFullModal = true;
        },
        closeStudentProfileModal() {
            this.showStudentProfileFullModal = false;
            this.selectedStudentId = null;
        },
        openReasonModal(action) {
            this.reasonModalAction = action;
            this.reasonText = '';
            this.showReasonModal = true;
        },
        closeReasonModal() {
            this.showReasonModal = false;
            this.reasonModalAction = '';
            this.reasonText = '';
        },
        openResubmitModal() {
            this.resubmitMessage = '';
            this.showResubmitModal = true;
        },
        closeResubmitModal() {
            this.showResubmitModal = false;
            this.resubmitMessage = '';
        },
        async submitReasonModal() {
            if (!this.reasonText.trim()) return;
            this.reasonSubmitting = true;
            try {
                if (this.reasonModalAction === 'reject-app') {
                    await this.$apollo.mutate({ mutation: REJECT_APP, variables: { id: this.application.id, reason: this.reasonText.trim() } });
                    await this.fetchApplication(this.application.id);
                } else if (this.reasonModalAction === 'return-app') {
                    await this.$apollo.mutate({ mutation: RETURN_APP, variables: { id: this.application.id, reason: this.reasonText.trim() } });
                    await this.fetchApplication(this.application.id);
                } else if (this.reasonModalAction === 'reject-hours' && this.hourSubmission) {
                    const submissionId = this.hourSubmission.id;
                    await this.$apollo.mutate({ mutation: REJECT_HOURS, variables: { id: submissionId, reason: this.reasonText.trim() } });
                    await this.fetchHourSubmission(submissionId);
                    if (this.application) await this.fetchApplication(this.application.id);
                } else if (this.reasonModalAction === 'return-hours' && this.hourSubmission) {
                    const submissionId = this.hourSubmission.id;
                    await this.$apollo.mutate({ mutation: RETURN_HOURS, variables: { id: submissionId, reason: this.reasonText.trim() } });
                    await this.fetchHourSubmission(submissionId);
                    if (this.application) await this.fetchApplication(this.application.id);
                }
                this.closeReasonModal();
            } catch (e) {
                if (this.$swal) this.$swal.fire({ icon: 'error', text: e.message || 'Action failed.' });
            }
            this.reasonSubmitting = false;
        },
        async submitResubmit() {
            this.resubmitting = true;
            try {
                await this.$apollo.mutate({
                    mutation: RESUBMIT_APP,
                    variables: { id: this.application.id, message: this.resubmitMessage.trim() || null },
                });
                await this.fetchApplication(this.application.id);
                this.closeResubmitModal();
            } catch (e) {
                if (this.$swal) this.$swal.fire({ icon: 'error', text: e.message || 'Resubmit failed.' });
            }
            this.resubmitting = false;
        },
        async fetchDetail() {
            this.loading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: DETAIL_QUERY,
                    variables: { token: this.token },
                    fetchPolicy: 'network-only',
                });
                const result = data.volunteeringOpportunityByToken;
                if (result.error) {
                    if (result.error_code === 403) {
                        this.$router.replace('/no-permission');
                        return;
                    }
                    if (result.error_code === 401) {
                        this.$router.replace('/');
                        return;
                    }
                    if (result.error_code === 404) {
                        this.$router.replace('/volunteering-and-rewards-hub');
                        return;
                    }
                    if (this.$swal) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Error',
                            text: result.message || 'Unable to load opportunity.',
                        });
                    }
                    return;
                }
                this.opportunity = result.data;
                if (this.opportunity.viewer_application_id) {
                    await this.fetchApplication(this.opportunity.viewer_application_id);
                }
                const appQuery = this.$route.query.application;
                const hoursQuery = this.$route.query.hours;
                // Deep-link from email/notification: load the exact application (API enforces access).
                if (appQuery) {
                    await this.fetchApplication(appQuery);
                }
                if (hoursQuery) {
                    await this.fetchHourSubmission(hoursQuery);
                }
            } catch (e) { console.log(e); }
            this.loading = false;
        },
        async fetchApplication(id) {
            const { data } = await this.$apollo.query({
                query: APPLICATION_QUERY,
                variables: { id: id },
                fetchPolicy: 'network-only',
            });
            if (data.volunteeringApplication && data.volunteeringApplication.data) {
                this.application = data.volunteeringApplication.data;
                if (this.application.hour_submission && !this.$route.query.hours) {
                    this.hourSubmission = this.application.hour_submission;
                }
            }
        },
        async fetchHourSubmission(id) {
            const { data } = await this.$apollo.query({
                query: HOUR_SUBMISSION_QUERY,
                variables: { id: String(id) },
                fetchPolicy: 'network-only',
            });
            const result = data.volunteeringHourSubmission;
            if (result.error || !result.data) {
                if (this.$swal) {
                    this.$swal.fire({ icon: 'error', title: 'Error', text: result.message || 'Hour submission not found.' });
                }
                return;
            }
            this.hourSubmission = result.data;
            if (this.hourSubmission.application_id && !this.application) {
                await this.fetchApplication(this.hourSubmission.application_id);
            }
        },
        async apply() {
            this.applying = true;
            try {
                const { data } = await this.$apollo.mutate({ mutation: APPLY_MUTATION, variables: { token: this.token } });
                var result = data.applyVolunteeringOpportunity;
                this.showApplyToast(
                    result && result.error ? 'error' : 'success',
                    (result && result.message) || (result && result.error ? 'Failed to apply.' : 'Application submitted.')
                );
                this.fetchDetail();
            } catch (e) {
                this.showApplyToast('error', (e && e.message) || 'Failed to apply.');
            }
            this.applying = false;
        },
        showApplyToast(icon, message) {
            if (!this.$swal) return;
            var Toast = this.$swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
            });
            Toast.fire({
                icon: icon || 'success',
                title: message || 'Success',
            });
        },
        async withdraw() {
            try {
                await this.$apollo.mutate({ mutation: WITHDRAW_MUTATION, variables: { id: this.application.id } });
                this.fetchDetail();
            } catch (e) { console.log(e); }
        },
        async approveApplication() {
            await this.$apollo.mutate({ mutation: APPROVE_APP, variables: { id: this.application.id } });
            this.fetchApplication(this.application.id);
        },
        addProofFile() { this.hoursForm.proof_files.push({ name: '', file: null }); },
        onProofFile(e, idx) { this.hoursForm.proof_files[idx].file = e.target.files[0]; },
        async submitHours() {
            this.submittingHours = true;
            const proofs = this.hoursForm.proof_files.filter(function (p) { return p.name && p.file; });
            try {
                const input = {
                    application_id: this.application.id,
                    selected_schedule_indices: this.hoursForm.selected.map(Number),
                    actual_hours: parseFloat(this.hoursForm.actual_hours),
                    notes: this.hoursForm.notes || null,
                    proof_files: proofs,
                };
                const { data } = await this.$apollo.mutate({ mutation: SUBMIT_HOURS, variables: { input: input } });
                if (this.$swal) this.$swal.fire({ icon: data.submitVolunteeringHours.error ? 'error' : 'success', text: data.submitVolunteeringHours.message });
                this.fetchApplication(this.application.id);
            } catch (e) { if (this.$swal) this.$swal.fire({ icon: 'error', text: e.message }); }
            this.submittingHours = false;
        },
        async approveHours() {
            if (!this.hourSubmission || !this.hourSubmission.id) return;
            const submissionId = this.hourSubmission.id;
            const { data } = await this.$apollo.mutate({ mutation: APPROVE_HOURS, variables: { id: submissionId } });
            if (data.approveVolunteeringHours.error) {
                if (this.$swal) this.$swal.fire({ icon: 'error', text: data.approveVolunteeringHours.message });
                return;
            }
            if (this.$swal) this.$swal.fire({ icon: 'success', text: data.approveVolunteeringHours.message });
            await this.fetchHourSubmission(submissionId);
            if (this.application && this.application.id) {
                await this.fetchApplication(this.application.id);
            }
        },
        formatStatus(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; },
        formatDateTime(d) { return d ? new Date(d).toLocaleString() : ''; },
        pad2(num) {
            return num < 10 ? '0' + num : String(num);
        },
        formatClock(date) {
            if (!date || isNaN(date.getTime())) return '';
            var hours = date.getHours();
            var minutes = this.pad2(date.getMinutes());
            var suffix = hours >= 12 ? 'pm' : 'am';
            var hour12 = hours % 12;
            if (hour12 === 0) hour12 = 12;
            return hour12 + ':' + minutes + ' ' + suffix;
        },
        formatScheduleSlot(slot) {
            if (!slot || !slot.start_at || !slot.end_at) return '';
            var start = new Date(slot.start_at);
            var end = new Date(slot.end_at);
            if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';
            var dateLabel = this.pad2(start.getDate()) + '/' + this.pad2(start.getMonth() + 1) + '/' + start.getFullYear();
            return dateLabel + ', ' + this.formatClock(start) + ' to ' + this.formatClock(end);
        },
        formatHours(h) { return parseFloat(h || 0).toFixed(1) + ' hrs'; },
        attachmentSourceName(att) {
            if (!att) return '';
            return String(att.original_filename || att.name || att.file_path || '').toLowerCase();
        },
        isImageAttachment(att, mime) {
            if (mime && String(mime).indexOf('image/') === 0) return true;
            return /\.(jpe?g|png)$/i.test(this.attachmentSourceName(att));
        },
        isPdfAttachment(att, mime) {
            if (mime === 'application/pdf') return true;
            return /\.pdf$/i.test(this.attachmentSourceName(att));
        },
        blobFromBase64(payload) {
            if (!payload || !payload.content) {
                throw new Error('File content is empty.');
            }
            var binaryString = atob(payload.content);
            var bytes = new Uint8Array(binaryString.length);
            for (var i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            var mime = payload.file_type || 'application/octet-stream';
            return {
                blob: new Blob([bytes], { type: mime }),
                mime: mime,
                filename: payload.filename || 'download',
            };
        },
        revokePreviewDisplayUrl() {
            if (this.previewDisplayUrl) {
                try {
                    URL.revokeObjectURL(this.previewDisplayUrl);
                } catch (e) { /* ignore */ }
            }
            this.previewDisplayUrl = null;
        },
        closeAttachmentPreview() {
            this.revokePreviewDisplayUrl();
            this.previewAttachment = null;
            this.previewKind = 'attachment';
            this.previewFileType = '';
            this.previewLoading = false;
            this.previewDownloading = false;
        },
        async fetchAttachmentPayload(att) {
            var response = await this.$apollo.mutate({
                mutation: ATTACHMENT_CONTENT_MUTATION,
                variables: {
                    opportunity_id: String(this.opportunity.id),
                    file_path: att.file_path,
                },
            });
            var payload = response.data && response.data.getVolunteeringOpportunityAttachmentContent;
            if (!payload || payload.error || !payload.data) {
                throw new Error((payload && payload.message) || 'Failed to load attachment.');
            }
            return payload.data;
        },
        async fetchProofPayload(proof) {
            var response = await this.$apollo.mutate({
                mutation: PROOF_CONTENT_MUTATION,
                variables: {
                    hour_submission_id: String(this.hourSubmission.id),
                    file_path: proof.file_path,
                },
            });
            var payload = response.data && response.data.getVolunteeringHourProofContent;
            if (!payload || payload.error || !payload.data) {
                throw new Error((payload && payload.message) || 'Failed to load proof file.');
            }
            return payload.data;
        },
        async openFilePreview(file, kind) {
            if (!file || !file.file_path) return;
            if (kind === 'attachment' && (!this.opportunity || !this.opportunity.id)) return;
            if (kind === 'proof' && (!this.hourSubmission || !this.hourSubmission.id)) return;

            this.revokePreviewDisplayUrl();
            this.previewKind = kind;
            this.previewAttachment = {
                name: file.name || '',
                file_path: file.file_path,
                original_filename: file.original_filename || '',
            };
            this.previewFileType = '';
            this.previewLoading = true;
            try {
                var data = kind === 'proof'
                    ? await this.fetchProofPayload(file)
                    : await this.fetchAttachmentPayload(file);
                var parsed = this.blobFromBase64(data);
                this.previewFileType = parsed.mime;
                if (!this.isImageAttachment(file, parsed.mime) && !this.isPdfAttachment(file, parsed.mime)) {
                    this.triggerBlobDownload(parsed.blob, parsed.filename);
                    this.closeAttachmentPreview();
                    return;
                }
                this.previewDisplayUrl = URL.createObjectURL(parsed.blob);
            } catch (e) {
                this.closeAttachmentPreview();
                if (this.$swal) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: (e && e.message) || 'Failed to load file.',
                    });
                }
            } finally {
                this.previewLoading = false;
            }
        },
        openAttachmentPreview(att) {
            return this.openFilePreview(att, 'attachment');
        },
        openProofPreview(proof) {
            return this.openFilePreview(proof, 'proof');
        },
        triggerBlobDownload(blob, filename) {
            var url = URL.createObjectURL(blob);
            var link = document.createElement('a');
            link.href = url;
            link.download = filename || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(function () { URL.revokeObjectURL(url); }, 60000);
        },
        async downloadPreviewAttachment() {
            if (!this.previewAttachment || !this.previewAttachment.file_path) return;
            this.previewDownloading = true;
            try {
                var data = this.previewKind === 'proof'
                    ? await this.fetchProofPayload(this.previewAttachment)
                    : await this.fetchAttachmentPayload(this.previewAttachment);
                var file = this.blobFromBase64(data);
                this.triggerBlobDownload(file.blob, file.filename);
            } catch (e) {
                if (this.$swal) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: (e && e.message) || 'Failed to download file.',
                    });
                }
            } finally {
                this.previewDownloading = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.vd-page {
    background: linear-gradient(180deg, #f8fafc 0%, #eef4ff 48%, #f8fafc 100%);
}
.vd-page__glow {
    pointer-events: none;
    position: absolute;
    inset: 0 0 auto 0;
    height: 22rem;
    background:
        radial-gradient(ellipse 70% 60% at 15% 0%, rgba(37, 99, 235, 0.12), transparent 70%),
        radial-gradient(ellipse 50% 50% at 90% 10%, rgba(14, 165, 233, 0.08), transparent 65%);
}

.vd-breadcrumb {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    font-size: 0.8125rem;
    color: #64748b;
}
.vd-breadcrumb__link {
    color: #64748b;
    font-weight: 500;
    text-decoration: none;
    &:hover { color: #1d4ed8; }
}
.vd-breadcrumb__sep {
    margin: 0 0.4rem;
    color: #cbd5e1;
}
.vd-breadcrumb__current {
    color: #0f172a;
    font-weight: 600;
}

.vd-hero {
    position: relative;
    overflow: hidden;
    border-radius: 1.25rem;
    background: #fff;
    border: 1px solid #e2e8f0;
    box-shadow: 0 10px 30px -18px rgba(15, 23, 42, 0.28);
}
.vd-hero__accent {
    height: 0.35rem;
    background: linear-gradient(90deg, #1e3a8a 0%, #2563eb 55%, #38bdf8 100%);
}
.vd-hero__inner {
    padding: 1.5rem 1.5rem 1.35rem;
}
.vd-hero__topline {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
}
.vd-hero__topline-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
}
.vd-hero__kicker {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin: 0;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #2563eb;
}
.vd-hero__title {
    margin: 0;
    font-size: 1.85rem;
    font-weight: 800;
    line-height: 1.2;
    letter-spacing: -0.02em;
    color: #0f172a;
}
.vd-hero__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.85rem;
}
.vd-hero__stats {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.75rem;
    margin-top: 1.25rem;
    padding-top: 1.15rem;
    border-top: 1px solid #f1f5f9;
}
@media (min-width: 640px) {
    .vd-hero__stats {
        grid-template-columns: repeat(3, 1fr);
    }
}
.vd-hero-stat {
    display: flex;
    align-items: flex-start;
    gap: 0.7rem;
    padding: 0.75rem 0.85rem;
    border-radius: 0.85rem;
    background: linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%);
    border: 1px solid #e8eef7;
}
.vd-hero-stat__icon {
    margin-top: 0.15rem;
    color: #2563eb;
    font-size: 0.9rem;
}
.vd-hero-stat__label {
    margin: 0;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #94a3b8;
}
.vd-hero-stat__value {
    margin: 0.15rem 0 0;
    font-size: 0.8125rem;
    font-weight: 650;
    color: #1e293b;
    line-height: 1.35;
}

.vd-back-link {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.45rem 0.8rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #475569;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 0.55rem;
    text-decoration: none;
    transition: background 0.15s ease, color 0.15s ease;
    &:hover { background: #f1f5f9; color: #0f172a; }
}

.vd-panel {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 1.1rem;
    overflow: hidden;
    box-shadow: 0 8px 24px -18px rgba(15, 23, 42, 0.35);
}
.vd-panel__header {
    padding: 1.15rem 1.35rem;
    border-bottom: 1px solid #f1f5f9;
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}
.vd-panel__title-wrap {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
}
.vd-panel__icon {
    width: 2.35rem;
    height: 2.35rem;
    border-radius: 0.7rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    color: #1d4ed8;
    font-size: 0.9rem;
}
.vd-panel__title {
    margin: 0;
    font-size: 1rem;
    font-weight: 750;
    color: #0f172a;
}
.vd-panel__subtitle {
    margin: 0.2rem 0 0;
    font-size: 0.8125rem;
    color: #64748b;
}
.vd-panel__body {
    padding: 1.25rem 1.35rem 1.4rem;
}

.vd-description {
    margin: 0 0 1.35rem;
    color: #334155;
    font-size: 0.9375rem;
    line-height: 1.75;
    white-space: pre-wrap;
}

.vd-detail-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.85rem;
}
@media (min-width: 640px) {
    .vd-detail-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}
.vd-detail-item {
    display: flex;
    gap: 0.85rem;
    padding: 0.95rem;
    border: 1px solid #e8eef7;
    border-radius: 0.9rem;
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}
.vd-detail-item__icon {
    width: 2.35rem;
    height: 2.35rem;
    border-radius: 0.7rem;
    background: #dbeafe;
    color: #2563eb;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 0.875rem;
}
.vd-detail-item__icon--location { background: #dbeafe; color: #1d4ed8; }
.vd-detail-item__icon--hours { background: #e0e7ff; color: #4338ca; }
.vd-detail-item__icon--deadline { background: #ffedd5; color: #c2410c; }
.vd-detail-item__icon--capacity { background: #d1fae5; color: #047857; }
.vd-detail-item__label {
    margin: 0;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #94a3b8;
}
.vd-detail-item__value {
    margin: 0.2rem 0 0;
    font-size: 0.875rem;
    font-weight: 650;
    color: #1e293b;
}
.vd-detail-item__sub {
    margin: 0.2rem 0 0;
    font-size: 0.75rem;
    color: #64748b;
    line-height: 1.4;
}
.vd-location-link {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    color: #2563eb;
    text-decoration: none;
    &:hover {
        color: #1d4ed8;
        text-decoration: underline;
    }
}
.vd-location-link__icon {
    font-size: 0.7rem;
    opacity: 0.85;
}

.vd-schedule-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}
.vd-schedule-item {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    padding: 0.95rem 1rem;
    border-radius: 0.9rem;
    border: 1px solid #e8eef7;
    background: #f8fafc;
}
.vd-schedule-item__num {
    width: 1.85rem;
    height: 1.85rem;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 0.75rem;
    font-weight: 700;
    color: #1d4ed8;
    background: #dbeafe;
}
.vd-schedule-item__date {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 650;
    color: #0f172a;
}
.vd-schedule-item__time {
    margin: 0.2rem 0 0;
    font-size: 0.8125rem;
    color: #64748b;
}

.vd-empty-inline {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 1rem;
    border-radius: 0.85rem;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    color: #64748b;
    font-size: 0.875rem;
    p { margin: 0; }
}

.vd-attachment-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
}
.vd-attachment-link {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.8rem 0.9rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.85rem;
    background: #fff;
    color: #1e293b;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
    &:hover {
        border-color: #93c5fd;
        background: #eff6ff;
        box-shadow: 0 6px 16px -12px rgba(37, 99, 235, 0.45);
    }
    &:disabled {
        opacity: 0.65;
        cursor: wait;
    }
}
.vd-attachment-link__icon {
    width: 2.1rem;
    height: 2.1rem;
    border-radius: 0.6rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #dbeafe;
    color: #1d4ed8;
    flex-shrink: 0;
}
.vd-attachment-link__text {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 0.875rem;
    font-weight: 600;
}
.vd-attachment-link__action {
    color: #64748b;
    font-size: 0.8rem;
}
.vd-attachment-fallback {
    display: block;
    padding: 0.75rem 0.9rem;
    border-radius: 0.75rem;
    background: #f8fafc;
    color: #475569;
    font-size: 0.875rem;
}

.vd-inline-action {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #f1f5f9;
}

.vd-sidebar {
    @media (min-width: 1024px) {
        position: sticky;
        top: 1.25rem;
        align-self: start;
    }
}
.vd-sidebar-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 1.1rem;
    padding: 1.2rem 1.25rem;
    box-shadow: 0 8px 24px -18px rgba(15, 23, 42, 0.35);
}
.vd-sidebar-card--action {
    background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
    border-color: #dbeafe;
}
.vd-sidebar-card__head {
    margin-bottom: 1rem;
}
.vd-sidebar-card__title {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 750;
    color: #0f172a;
}
.vd-sidebar-card__sub {
    margin: 0.25rem 0 0;
    font-size: 0.8125rem;
    color: #64748b;
    line-height: 1.4;
}
.vd-student-link {
    cursor: pointer;
    color: #1d4ed8;
    font-weight: 650;
    transition: color 0.15s ease;
    &:hover {
        color: #1e40af;
        text-decoration: underline;
    }
}
.vd-sidebar-actions {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
}
.vd-sidebar-status {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.vd-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.28rem 0.65rem;
    border-radius: 9999px;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    background: #e2e8f0;
    color: #475569;
}
.vd-badge--lg {
    font-size: 0.75rem;
    padding: 0.4rem 0.8rem;
}
.vd-badge__dot {
    width: 0.4rem;
    height: 0.4rem;
    border-radius: 9999px;
    background: currentColor;
}
.vd-badge--draft { background: #f1f5f9; color: #475569; }
.vd-badge--published { background: #dbeafe; color: #1d4ed8; }
.vd-badge--closed { background: #fee2e2; color: #b91c1c; }
.vd-badge--completed { background: #d1fae5; color: #047857; }
.vd-badge--archived { background: #e2e8f0; color: #64748b; }
.vd-badge--open { background: #d1fae5; color: #047857; }
.vd-badge--ended { background: #ffedd5; color: #c2410c; }
.vd-badge--pending { background: #fef3c7; color: #b45309; }
.vd-badge--approved { background: #d1fae5; color: #047857; }
.vd-badge--rejected { background: #fee2e2; color: #b91c1c; }
.vd-badge--returned { background: #e0e7ff; color: #4338ca; }
.vd-badge--withdrawn { background: #f1f5f9; color: #64748b; }

.vd-btn-primary,
.vd-btn-outline,
.vd-btn-danger,
.vd-btn-ghost {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    min-height: 2.5rem;
    padding: 0.55rem 1rem;
    border-radius: 0.65rem;
    font-size: 0.875rem;
    font-weight: 650;
    cursor: pointer;
    border: 1px solid transparent;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}
.vd-btn-primary {
    color: #fff;
    background: linear-gradient(180deg, #1e3a8a 0%, #2563eb 100%);
    box-shadow: 0 8px 18px -10px rgba(37, 99, 235, 0.65);
    &:hover:not(:disabled) {
        background: linear-gradient(180deg, #1e40af 0%, #1d4ed8 100%);
    }
}
.vd-btn-outline {
    color: #1e40af;
    background: #fff;
    border-color: #bfdbfe;
    &:hover:not(:disabled) {
        background: #eff6ff;
        border-color: #93c5fd;
    }
}
.vd-btn-danger {
    color: #fff;
    background: linear-gradient(180deg, #b91c1c 0%, #dc2626 100%);
    &:hover:not(:disabled) {
        background: linear-gradient(180deg, #991b1b 0%, #b91c1c 100%);
    }
}
.vd-btn-ghost {
    color: #475569;
    background: #fff;
    border-color: #cbd5e1;
    &:hover:not(:disabled) {
        background: #f8fafc;
    }
}

.vd-muted {
    margin: 0;
    font-size: 0.875rem;
    color: #64748b;
    line-height: 1.5;
}
.vd-notes-block {
    margin: 1rem 0 0;
    padding: 0.9rem 1rem;
    border-radius: 0.75rem;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    font-size: 0.875rem;
    color: #334155;
    line-height: 1.5;
}

.vd-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
.vd-form-card {
    padding: 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.85rem;
    background: #fafbfc;
}
.vd-form-card__title {
    margin: 0 0 0.75rem;
    font-size: 0.875rem;
    font-weight: 700;
    color: #0f172a;
}
.vd-form-row {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}
.vd-form-actions {
    display: flex;
    justify-content: flex-end;
}
.vd-label {
    font-size: 0.75rem;
    font-weight: 700;
    color: #475569;
}
.vd-hint {
    margin: 0.25rem 0 0;
    font-size: 0.75rem;
    color: #64748b;
}
.vd-input {
    padding: 0.55rem 0.75rem;
    font-size: 0.8125rem;
    color: #1e293b;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 0.55rem;
    &:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
    }
}
.vd-file-input {
    font-size: 0.75rem;
    color: #64748b;
}
.vd-checkbox-row {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    padding: 0.45rem 0;
    font-size: 0.8125rem;
    color: #334155;
}
.vd-proof-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.55rem;
}
.vd-icon-btn {
    width: 2rem;
    height: 2rem;
    border-radius: 0.5rem;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #64748b;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.vd-icon-btn--danger {
    color: #dc2626;
    border-color: #fecaca;
    &:hover { background: #fef2f2; }
}

.vd-timeline {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
}
.vd-timeline-item {
    position: relative;
    padding-left: 1.1rem;
}
.vd-timeline-dot {
    position: absolute;
    left: 0;
    top: 0.35rem;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 9999px;
    background: #2563eb;
    box-shadow: 0 0 0 3px #dbeafe;
}
.vd-timeline-title {
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 650;
    color: #0f172a;
}
.vd-timeline-note {
    margin: 0.15rem 0 0;
    font-size: 0.8125rem;
    color: #64748b;
}
.vd-timeline-date {
    margin: 0.15rem 0 0;
    font-size: 0.6875rem;
    color: #94a3b8;
}

.vd-modal-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 0.9rem;
    padding: 1rem 1.125rem;
}
.vd-modal-footer-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
}

.vd-preview-scroll {
    display: flex;
    flex-direction: column;
    min-height: 60vh;
    height: 100%;
    padding: 1.25rem 1.5rem;
    background: linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%);
}
.vd-preview-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
    flex-shrink: 0;
}
.vd-preview-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.35rem 0.75rem;
    border-radius: 9999px;
    background: #dbeafe;
    color: #1d4ed8;
    font-size: 0.75rem;
    font-weight: 600;
}
.vd-preview-frame {
    flex: 1 1 auto;
    min-height: 24rem;
    border-radius: 1rem;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
}
.vd-preview-frame--image {
    padding: 1rem;
    background-color: #f1f5f9;
}
.vd-preview-frame--pdf {
    background: #fff;
    padding: 0;
    align-items: stretch;
}
.vd-preview-img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
}
.vd-preview-iframe {
    width: 100%;
    height: 100%;
    min-height: 24rem;
    border: 0;
}
.vd-preview-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 2rem;
    color: #64748b;
    font-size: 0.875rem;
    font-weight: 600;
}
.vd-preview-state__icon {
    font-size: 1.5rem;
    color: #2563eb;
}
.vd-preview-state__icon--warn {
    color: #d97706;
}
</style>
